import React, { useEffect, useMemo, useState } from "react";
import { Check, Search, Info } from "lucide-react";
import { Spinner } from "./ui/kit";
import { KartaSekce } from "./DetailHlava";
import api, { apiError } from "../lib/api";
import { ikonaTechnologie, zkratkaTechnologie, useTechnologie } from "../lib/technologie";

/**
 * Specializace a přístup člena týmu (plátno 17A).
 *   • Specializace — dlaždice technologií, které umí (víc najednou). Technik
 *     i subdodavatel pak vidí jen technologie a zařízení svých oborů.
 *   • Rozsah — jen subdodavatel: servis, nebo servis + obchod.
 *   • Přístup ke klientům — jen subdodavatel: klienti se přefiltrují podle
 *     specializace, štítky ukazují jejich technologie (jeho obory černě).
 * Stav drží `useSpecializace`, karty jsou jen pohledy (na telefonu jsou nad
 * seznamem klientů, na počítači v pravém sloupci).
 */
/** Omezuje se jen čistý technik / subdodavatel — kdo má i jinou roli (obchodník, majitel), vidí vše (pristup.obory). */
export const MA_SPECIALIZACI = (roles) => !!roles?.length && roles.every((r) => r === "TECHNIK" || r === "SUBDODAVATEL");

export function useSpecializace(member, onSaved, zapnuto = true) {
  const sub = (member?.roles || []).includes("SUBDODAVATEL");
  const id = zapnuto && MA_SPECIALIZACI(member?.roles) ? member?.id : null;
  const technologie = useTechnologie();
  const [puvodni, setPuvodni] = useState(null);      // {specializace, pristup_klienti, rozsah_sub}
  const [spec, setSpec] = useState([]);
  const [sel, setSel] = useState([]);
  const [rozsah, setRozsah] = useState("SERVIS");
  const [klienti, setKlienti] = useState(null);
  const [katKlientu, setKatKlientu] = useState({});
  const [ukladam, setUkladam] = useState(false);
  const [chyba, setChyba] = useState("");
  const [ulozeno, setUlozeno] = useState(false);

  useEffect(() => {
    if (!id) return;
    api.get(`/api/team/${id}/pristup`).then(({ data }) => {
      setPuvodni(data);
      setSpec(data.specializace || []);
      setSel(data.pristup_klienti || []);
      setRozsah(data.rozsah_sub || "SERVIS");
    }).catch(() => setPuvodni({ specializace: [], pristup_klienti: [], rozsah_sub: "SERVIS" }));
    if (sub) {
      api.get("/api/clients", { params: { limit: 200 } }).then(({ data }) => setKlienti(data.items || [])).catch(() => setKlienti([]));
      api.get("/api/team/kategorie-klientu").then(({ data }) => setKatKlientu(data.items || {})).catch(() => {});
    }
  }, [id, sub]);

  const zmeneno = !!puvodni && (
    JSON.stringify([...spec].sort()) !== JSON.stringify([...(puvodni.specializace || [])].sort())
    || (sub && (JSON.stringify([...sel].sort()) !== JSON.stringify([...(puvodni.pristup_klienti || [])].sort())
      || rozsah !== (puvodni.rozsah_sub || "SERVIS"))));

  const prepniSpec = (id) => setSpec((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const prepniKlienta = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const ulozit = async () => {
    setUkladam(true); setChyba("");
    try {
      const body = { specializace: spec, ...(sub ? { pristup_klienti: sel, rozsah_sub: rozsah } : {}) };
      const { data } = await api.patch(`/api/team/${id}/pristup`, body);
      setPuvodni({ ...puvodni, ...data });
      setSpec(data.specializace || []);
      if (sub) setSel(data.pristup_klienti || []);
      setUlozeno(true); setTimeout(() => setUlozeno(false), 2200);
      onSaved && onSaved();
    } catch (e) {
      setChyba(apiError(e.response?.data?.detail) || e.message);
    } finally { setUkladam(false); }
  };

  return { sub, technologie, nacteno: !!puvodni && technologie !== null, spec, prepniSpec, sel, prepniKlienta,
    rozsah, setRozsah, klienti, katKlientu, zmeneno, ulozit, ukladam, chyba, ulozeno };
}

function UlozitTlacitko({ s, testid }) {
  if (s.ulozeno && !s.zmeneno) return <span className="t-meta font-600 text-success" data-testid={`${testid}-ulozeno`}>Uloženo</span>;
  if (!s.zmeneno) return null;
  return (
    <button type="button" onClick={s.ulozit} disabled={s.ukladam} data-testid={testid}
      className="tap md:min-h-0 h-9 px-4 rounded-full bg-accent text-white text-[13px] font-700 inline-flex items-center gap-1.5 disabled:opacity-60">
      {s.ukladam ? <Spinner /> : "Uložit"}
    </button>
  );
}

/** Dlaždice technologií se zaškrtnutím (víc najednou). Sdílí karta člena i pozvánka. */
export function VyberTechnologii({ technologie, value, onToggle }) {
  return (
    <div className="grid grid-cols-2 gap-2" data-testid="vyber-technologii">
      {(technologie || []).map((t) => {
        const on = value.includes(t.id);
        const Ikona = ikonaTechnologie(t.icon);
        return (
          <button key={t.id} type="button" onClick={() => onToggle(t.id)} aria-pressed={on} data-testid={`clen-spec-${t.id}`}
            className={`min-w-0 min-h-12 md:min-h-[46px] rounded-[14px] pl-3 pr-2 py-1.5 flex items-center gap-2 text-left text-[13.5px] md:text-[13px] leading-[1.2] font-700 transition-colors ${
              on ? "bg-ink text-white" : "bg-surface text-sec hover:text-ink"}`}>
            <Ikona size={16} strokeWidth={1.9} className="shrink-0" />
            <span className="flex-1 min-w-0 line-clamp-2 break-words">{t.name}</span>
            {on && (
              <span className="h-[18px] w-[18px] rounded-full bg-accent text-white inline-flex items-center justify-center shrink-0">
                <Check size={11} strokeWidth={3} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function KartaSpecializace({ s }) {
  const pocet = s.spec.length;
  return (
    <KartaSekce testid="clen-specializace" titul="Specializace" pocet={s.nacteno ? (pocet ? `${pocet} z ${s.technologie.length}` : "vidí vše") : null}
      akce={<span className="inline-flex items-center gap-2">
        <UlozitTlacitko s={s} testid="clen-specializace-ulozit" />
        <span title="Uvidí jen tyto technologie v obchodu a jen zařízení těchto technologií v servisu. Bez specializace vidí vše."
          className="tap md:min-h-0 inline-flex items-center justify-center text-muted cursor-help" aria-label="Uvidí jen tyto technologie v obchodu a jen zařízení těchto technologií v servisu. Bez specializace vidí vše.">
          <Info size={15} />
        </span>
      </span>} telo>
      {!s.nacteno ? <div className="py-4 flex justify-center"><Spinner /></div> : (
        <>
          <VyberTechnologii technologie={s.technologie} value={s.spec} onToggle={s.prepniSpec} />
          {!s.sub && s.chyba && <p className="t-meta text-danger mt-2">{s.chyba}</p>}
        </>
      )}
    </KartaSekce>
  );
}

export function KartaRozsah({ s }) {
  return (
    <KartaSekce testid="clen-rozsah" titul="Rozsah" telo>
      <span className="inline-flex gap-0.5 p-[3px] rounded-full bg-surface" data-testid="pristup-rozsah">
        {[["SERVIS", "Servis"], ["OBCHOD", "Servis + obchod"]].map(([v, t]) => (
          <button key={v} type="button" onClick={() => s.setRozsah(v)} data-testid={`pristup-rozsah-${v}`}
            className={`h-10 md:h-[30px] px-3.5 rounded-full t-meta md:text-[12.5px] font-700 ${s.rozsah === v ? "bg-ink text-white" : "text-sec hover:text-ink"}`}>{t}</button>
        ))}
      </span>
      <p className="t-meta text-muted mt-2">
        {s.rozsah === "OBCHOD"
          ? "Vidí i obchodní případy a nabídky svých oborů — jen prodejní ceny. Odeslat a schválit nabídku můžeš ty nebo obchodník."
          : "Jen servis a montáže svých oborů: schůzky, zařízení a servisní zápisy."}
      </p>
    </KartaSekce>
  );
}

function RadekKlienta({ c, s, kat, techMap }) {
  const on = s.sel.includes(c.id);
  return (
    <button type="button" onClick={() => s.prepniKlienta(c.id)} aria-pressed={on} data-testid={`clientpicker-item-${c.id}`}
      className="w-full min-h-[56px] flex items-center gap-3 py-2.5 border-t border-line-soft text-left hover:bg-inputbg md:px-1">
      <span className={`h-6 w-6 rounded-[8px] inline-flex items-center justify-center shrink-0 ${
        on ? "bg-accent text-white" : "bg-white text-transparent shadow-[inset_0_0_0_1.5px_#d5d3ce]"}`} data-testid={`clientpicker-check-${c.id}`}>
        <Check size={14} strokeWidth={3} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block t-telo md:text-[14.5px] font-600 text-ink truncate">{c.display_name}</span>
        <span className="flex flex-wrap gap-1 mt-1">
          {kat.length === 0 && <span className="t-meta text-muted">zatím bez technologie</span>}
          {kat.map((id) => techMap[id] && (
            <span key={id} className={`h-5 px-2 rounded-full text-[10.5px] font-700 inline-flex items-center ${
              !s.spec.length || s.spec.includes(id) ? "bg-ink text-white" : "bg-surface text-muted-2"}`}>{zkratkaTechnologie(techMap[id])}</span>
          ))}
        </span>
      </span>
      {c.adresa?.mesto && <span className="hidden sm:inline t-meta text-muted shrink-0">{c.adresa.mesto}</span>}
    </button>
  );
}

export function KartaPristupKlientu({ s }) {
  const [q, setQ] = useState("");
  const [ostatni, setOstatni] = useState(false);
  const techMap = useMemo(() => Object.fromEntries((s.technologie || []).map((t) => [t.id, t])), [s.technologie]);
  const { jeho, zbytek } = useMemo(() => {
    const ql = q.trim().toLowerCase();
    const vse = (s.klienti || []).filter((c) => !ql || (c.display_name || "").toLowerCase().includes(ql)
      || (c.adresa?.mesto || "").toLowerCase().includes(ql));
    // Bez specializace jsou „jeho" všichni klienti.
    const umi = (c) => !s.spec.length || (s.katKlientu[c.id] || []).some((k) => s.spec.includes(k));
    // Vybraní klienti zůstávají nahoře i bez jeho oborů — ať jde přístup odebrat.
    return { jeho: vse.filter((c) => umi(c) || s.sel.includes(c.id)), zbytek: vse.filter((c) => !umi(c) && !s.sel.includes(c.id)) };
  }, [s.klienti, s.katKlientu, s.spec, s.sel, q]);

  return (
    <KartaSekce testid="clen-pristup" titul="Přístup ke klientům" pocet={`${s.sel.length} vybráno`}
      akce={<UlozitTlacitko s={s} testid="pristup-save" />} telo>
      {s.klienti === null ? <div className="py-6 flex justify-center"><Spinner /></div> : (
        <>
          <label className="h-11 rounded-full bg-surface flex items-center gap-2.5 px-4 text-sec mb-2">
            <Search size={16} strokeWidth={2.2} className="shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat klienta"
              className="flex-1 min-w-0 bg-transparent outline-none text-[16px] md:text-[14px] text-ink placeholder:text-sec" data-testid="clientpicker-search" />
          </label>
          {jeho.map((c) => <RadekKlienta key={c.id} c={c} s={s} kat={s.katKlientu[c.id] || []} techMap={techMap} />)}
          {jeho.length === 0 && (
            <p className="py-5 t-meta text-muted text-center border-t border-line-soft" data-testid="pristup-zadny">
              {q ? "Žádný klient neodpovídá." : "Žádný klient zatím nemá jeho technologie."}
            </p>
          )}
          {zbytek.length > 0 && (
            <button type="button" onClick={() => setOstatni((v) => !v)} aria-pressed={ostatni} data-testid="pristup-ostatni"
              className="tap w-full flex items-center gap-2.5 pt-3.5 pb-1 border-t border-line-soft t-meta md:text-[13.5px] font-600 text-sec">
              <span className={`relative h-[22px] w-[38px] rounded-full shrink-0 transition-colors ${ostatni ? "bg-success" : "bg-line-strong"}`}>
                <span className={`absolute top-[2px] left-[2px] h-[18px] w-[18px] rounded-full bg-white shadow transition-transform duration-200 motion-reduce:transition-none ${ostatni ? "translate-x-4" : ""}`} />
              </span>
              Ukázat i ostatní klienty <span className="text-muted font-500">({zbytek.length})</span>
            </button>
          )}
          {ostatni && zbytek.length > 0 && (
            <div className="rise" data-testid="pristup-ostatni-seznam">
              <div className="label-up text-muted pt-3 pb-1">Bez jeho oborů — uvidí jen klienta, žádnou technologii</div>
              {zbytek.map((c) => <RadekKlienta key={c.id} c={c} s={s} kat={s.katKlientu[c.id] || []} techMap={techMap} />)}
            </div>
          )}
          {s.chyba && <p className="t-meta text-danger mt-2">{s.chyba}</p>}
        </>
      )}
    </KartaSekce>
  );
}
